/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useEffect, useState } from 'react';
import { FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material';
import useMyExpectedOrders, { ExpectedOrdersConversion } from '../gql/myExpectedOrders';
import getCurrentBusinessQuarter from '../../../utils/getCurrentBusinessQuarter';
import useErrorHandler from '../../../utils/userErrorHandler';
import getYearsForSelect from '../../../utils/getYearsForSelect';
import months from '../../../utils/months';

type ExpectedOrdersProps = {
    quarterly?: boolean;
    upliftData: (data: ExpectedOrdersConversion) => void;
    comparisonBlockData: ExpectedOrdersConversion;
};

const ExpectedOrders: React.FC<ExpectedOrdersProps> = ({ quarterly, upliftData, comparisonBlockData }) => {
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [quarter, setQuarter] = useState(getCurrentBusinessQuarter());
    const myExpectedOrders = useMyExpectedOrders({ year: year, month: month, quarter: quarter });
    useErrorHandler(myExpectedOrders.error);

    useEffect(() => {
        myExpectedOrders.refetch();
    }, [year, month, quarter]);

    useEffect(() => {
        if (myExpectedOrders.data) upliftData(myExpectedOrders.data);
    }, [myExpectedOrders.data]);

    const getProgressionPercentage = (progress: number, target: number): number => {
        return Math.floor((progress / target) * 100);
    };

    return (
        <div style={{ width: '100%' }}>
            <FormControl style={{ width: '50%' }}>
                <InputLabel id="year-select">Év</InputLabel>
                <Select label="Év" id="year-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                    {getYearsForSelect().map((year) => (
                        <MenuItem key={year} value={year}>
                            {year}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {!quarterly && (
                <FormControl style={{ width: '50%' }}>
                    <InputLabel id="month-select">Hónap</InputLabel>
                    <Select label="Hónap" id="month-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                        {months.map((month) => (
                            <MenuItem key={month.number} value={month.number}>
                                {month.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {quarterly && (
                <FormControl style={{ width: '50%' }}>
                    <InputLabel id="quarter-select">Negyedév</InputLabel>
                    <Select label="Negyedév" id="quarter-select" value={quarter} onChange={(e) => setQuarter(Number(e.target.value))}>
                        <MenuItem value={1}>Első negyedév</MenuItem>
                        <MenuItem value={2}>Második negyedév</MenuItem>
                        <MenuItem value={3}>Harmadik negyedév</MenuItem>
                        <MenuItem value={4}>Negyedik negyedév</MenuItem>
                    </Select>
                </FormControl>
            )}

            {!quarterly && (
                <div css={resultsDisplayStyles}>
                    <Typography>
                        Havi konvertált rendelések: {myExpectedOrders.data?.monthly.orders}{' '}
                        <span style={{ fontWeight: 700, color: getProgressionPercentage(myExpectedOrders.data?.monthly.orders as number, myExpectedOrders.data?.monthly.expectedOrders as number) >= 100 ? '#2e7d32' : '#d32f2f' }}>
                            ({getProgressionPercentage(myExpectedOrders.data?.monthly.orders as number, myExpectedOrders.data?.monthly.expectedOrders as number)}%)
                        </span>
                    </Typography>
                    <Typography>Havi várható rendelések: {myExpectedOrders.data?.monthly.expectedOrders}</Typography>
                </div>
            )}

            {quarterly && (
                <div css={resultsDisplayStyles}>
                    <Typography>
                        Negyedéves konvertált rendelések: {myExpectedOrders.data?.quarterly.orders}{' '}
                        <span style={{ fontWeight: 700, color: getProgressionPercentage(myExpectedOrders.data?.quarterly.orders as number, myExpectedOrders.data?.quarterly.expectedOrders as number) >= 100 ? '#2e7d32' : '#d32f2f' }}>
                            ({getProgressionPercentage(myExpectedOrders.data?.quarterly.orders as number, myExpectedOrders.data?.quarterly.expectedOrders as number)}%)
                        </span>
                    </Typography>
                    <Typography>Negyedéves várható rendelések: {myExpectedOrders.data?.quarterly.expectedOrders}</Typography>
                </div>
            )}

            {!quarterly && (myExpectedOrders.data?.monthly.orders as number) > comparisonBlockData.monthly.orders && (
                <div css={resultContainerStyles}>
                    <Typography fontWeight={700} variant="body1" style={{ color: '#2e7d32' }}>
                        Ebben az időszakban ennyivel:
                    </Typography>
                    <Typography fontWeight={700} variant="h4" style={{ color: '#2e7d32' }}>
                        +{(myExpectedOrders.data?.monthly.orders as number) - comparisonBlockData.monthly.orders}
                    </Typography>
                    <Typography fontWeight={700} variant="body1" style={{ color: '#2e7d32' }}>
                        több volt a rendelés!
                    </Typography>
                </div>
            )}

            {quarterly && (myExpectedOrders.data?.quarterly.orders as number) > comparisonBlockData.quarterly.orders && (
                <div css={resultContainerStyles}>
                    <Typography fontWeight={700} variant="body1" style={{ color: '#2e7d32' }}>
                        Ebben az időszakban ennyivel:
                    </Typography>
                    <Typography fontWeight={700} variant="h4" style={{ color: '#2e7d32' }}>
                        +{(myExpectedOrders.data?.quarterly.orders as number) - comparisonBlockData.quarterly.orders}
                    </Typography>
                    <Typography fontWeight={700} variant="body1" style={{ color: '#2e7d32' }}>
                        több volt a rendelés!
                    </Typography>
                </div>
            )}
        </div>
    );
};

const resultsDisplayStyles = css`
    display: flex;
    flex-direction: column;
    padding-top: 20px;

    & > * + * {
        margin-top: 20px !important;
    }
`;

const resultContainerStyles = css`
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 20px;
    box-sizing: border-box;
    & > * + * {
        margin-top: 10px !important;
    }
`;

export default ExpectedOrders;
